import { createContext, FC, MutableRefObject, ReactNode, useMemo, useRef, useState } from "react";

import { IApiArgument, IDynamicForm, IForm, IFormInputs } from "config/types";

interface IDynamicFormContext {
  dynamicForm: IDynamicForm;
  form: IForm;
  inputs: IFormInputs;
  modalOverlay: {
    shouldCloseModalOverlay: boolean;
    closeModalOverlay?(): void;
  };
  mutationResponses: MutableRefObject<Record<IApiArgument["source"], unknown>>;
}

const defaultContextValue: IDynamicFormContext = {
  dynamicForm: {
    description: "",
    flow: { slug: "" },
    id: "",
    groups: [],
    items: [],
    layout: "vertical",
    leadingNavigationButton: undefined,
    slug: "",
  },
  form: {
    allRequiredFieldsFilled: false,
    formErrorMessage: "",
    isFormDirtied: false,
    isFormInitialized: false,
    loading: false,
    valid: false,
    getFormInput: () => ({}),
    getInputError: () => false,
    getInputErrorMessage: () => [],
    getSubmitBtnDisabled: () => false,
    initializeFormState: () => ({}),
    onBlur: () => ({}),
    onChange: () => ({}),
    onSubmit: () => {},
    processServerValidationErrors: () => ({}),
    reset: () => ({}),
    resetInputError: () => ({}),
    setFormErrorMessage: () => ({}),
    setLoading: () => ({}),
    setInputs: () => ({}),
    validateAllFields: () => false,
  },
  inputs: {},
  modalOverlay: {
    shouldCloseModalOverlay: false,
    closeModalOverlay: () => ({}),
  },
  mutationResponses: { current: {} },
};

export const DynamicFormContext = createContext(defaultContextValue);

export const DynamicFormProvider: FC<
  Pick<IDynamicFormContext, "dynamicForm" | "form" | "inputs"> & { children?: ReactNode }
> = ({ children, dynamicForm, form, inputs }) => {
  const [shouldCloseModalOverlay, setShouldCloseModalOverlay] = useState(false);
  const mutationResponses = useRef<Record<IApiArgument["source"], unknown>>({});

  const closeModalOverlay = () => {
    setShouldCloseModalOverlay(true);
  };

  const contextValue = useMemo(
    () => ({
      dynamicForm,
      form,
      inputs,
      modalOverlay: { shouldCloseModalOverlay, closeModalOverlay },
      mutationResponses: mutationResponses,
    }),
    [dynamicForm, form, inputs, shouldCloseModalOverlay]
  );

  return <DynamicFormContext.Provider value={contextValue}>{children}</DynamicFormContext.Provider>;
};
